// src/components/vocabulary/SwipeIndicators.jsx
// Visual indicators for swipe gestures on vocabulary cards
import React from 'react';
import { ChevronLeft, ChevronRight, X, Check, RotateCcw, Zap } from 'lucide-react';

/**
 * Swipe Indicators Component
 * Shows visual hints for swipe directions and their meanings
 */
const SwipeIndicators = ({
  isDragging,
  dragOffset,
  isFlipped,
  showHints = true,
  currentRating = null
}) => {
  // Calculate opacity based on drag distance
  const threshold = 50;
  const leftOpacity = isDragging ? Math.min(Math.abs(Math.min(dragOffset.x, 0)) / threshold, 1) : 0;
  const rightOpacity = isDragging ? Math.min(Math.max(dragOffset.x, 0) / threshold, 1) : 0;

  // Determine which indicator is active
  const isLeftActive = dragOffset.x < -threshold;
  const isRightActive = dragOffset.x > threshold;

  // Don't show indicators when card is flipped
  if (isFlipped && !isDragging) return null;

  return (
    <>
      {/* Left Indicator - "Again" */}
      <div
        className={`
          absolute left-0 top-1/2 -translate-y-1/2 
          pointer-events-none z-20
          transition-all duration-300 ease-out
          ${isDragging ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          transform: `translateY(-50%) translateX(${isDragging ? '0' : '-20px'})`,
        }}
      >
        <div
          className={`
            flex items-center gap-2 px-4 py-3 rounded-r-2xl
            transition-all duration-200
            ${isLeftActive 
              ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-110' 
              : 'bg-red-100 text-red-600'
            }
          `}
          style={{ opacity: leftOpacity }}
        >
          <div className="relative">
            <RotateCcw className={`w-5 h-5 ${isLeftActive ? 'animate-spin' : ''}`} />
            {isLeftActive && (
              <div className="absolute inset-0 animate-ping">
                <RotateCcw className="w-5 h-5 opacity-75" />
              </div>
            )}
          </div>
          <span className="font-semibold text-sm whitespace-nowrap">
            Nochmal
          </span>
          <ChevronLeft className="w-4 h-4" />
        </div>

        {/* Glow effect when active */}
        {isLeftActive && (
          <div
            className="absolute inset-0 rounded-r-2xl bg-red-500 opacity-20 blur-xl animate-pulse"
            style={{ transform: 'scale(1.5)' }}
          />
        )}
      </div>

      {/* Right Indicator - "Good" */}
      <div
        className={`
          absolute right-0 top-1/2 -translate-y-1/2 
          pointer-events-none z-20
          transition-all duration-300 ease-out
          ${isDragging ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          transform: `translateY(-50%) translateX(${isDragging ? '0' : '20px'})`,
        }}
      >
        <div
          className={`
            flex items-center gap-2 px-4 py-3 rounded-l-2xl
            transition-all duration-200
            ${isRightActive 
              ? 'bg-green-500 text-white shadow-lg shadow-green-500/30 scale-110' 
              : 'bg-green-100 text-green-600'
            }
          `}
          style={{ opacity: rightOpacity }}
        >
          <ChevronRight className="w-4 h-4" />
          <span className="font-semibold text-sm whitespace-nowrap">
            Gut
          </span>
          <div className="relative">
            <Check className={`w-5 h-5 ${isRightActive ? 'animate-bounce' : ''}`} />
            {isRightActive && (
              <div className="absolute inset-0 animate-ping">
                <Check className="w-5 h-5 opacity-75" />
              </div>
            )}
          </div>
        </div>

        {/* Glow effect when active */}
        {isRightActive && (
          <div
            className="absolute inset-0 rounded-l-2xl bg-green-500 opacity-20 blur-xl animate-pulse"
            style={{ transform: 'scale(1.5)' }}
          />
        )}
      </div>

      {/* Edge Glow Effects */}
      {isDragging && (
        <>
          {/* Left edge glow */}
          <div
            className="absolute left-0 top-0 bottom-0 w-32 pointer-events-none"
            style={{
              background: `linear-gradient(to right, rgba(239, 68, 68, ${leftOpacity * 0.2}), transparent)`,
              boxShadow: isLeftActive ? 'inset 10px 0 30px rgba(239, 68, 68, 0.3)' : 'none'
            }}
          />

          {/* Right edge glow */}
          <div
            className="absolute right-0 top-0 bottom-0 w-32 pointer-events-none"
            style={{
              background: `linear-gradient(to left, rgba(34, 197, 94, ${rightOpacity * 0.2}), transparent)`,
              boxShadow: isRightActive ? 'inset -10px 0 30px rgba(34, 197, 94, 0.3)' : 'none'
            }}
          />
        </>
      )}

      {/* Initial Hint (shows when not dragging) */}
      {showHints && !isDragging && !isFlipped && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="flex items-center gap-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <ChevronLeft className="w-3 h-3" />
              <X className="w-3 h-3 text-red-500" />
              <span>Wischen</span>
            </div>
            <div className="w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Wischen</span>
              <Check className="w-3 h-3 text-green-500" />
              <ChevronRight className="w-3 h-3" />
            </div>
          </div>
        </div>
      )}

      {/* Card Shadow/Preview Effect */}
      {isDragging && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `translateX(${dragOffset.x * 0.1}px) rotateZ(${dragOffset.x * 0.02}deg)`,
            opacity: 0.1
          }}
        >
          <div className="w-full h-full bg-black rounded-2xl blur-xl" />
        </div>
      )}
    </>
  );
};

/**
 * Compact Swipe Hint Component
 * Shows at the top of the card stack
 */
export const SwipeHint = ({ visible = true }) => {
  return null;
};

/**
 * Drag Direction Indicator
 * Shows current drag direction with visual feedback
 */
export const DragDirectionIndicator = ({ dragOffset, isDragging }) => {
  if (!isDragging || Math.abs(dragOffset.x) < 20) return null;

  const isLeft = dragOffset.x < 0;
  const intensity = Math.min(Math.abs(dragOffset.x) / 100, 1);

  return (
    <div
      className={`
        absolute top-4 ${isLeft ? 'left-4' : 'right-4'}
        pointer-events-none z-30
        animate-fade-in
      `}
    >
      <div
        className={`
          px-3 py-1.5 rounded-lg font-bold text-sm
          transform transition-all duration-200
          ${isLeft 
            ? 'bg-red-500 text-white shadow-red-500/50' 
            : 'bg-green-500 text-white shadow-green-500/50'
          }
        `}
        style={{
          opacity: intensity,
          transform: `scale(${0.8 + intensity * 0.2})`,
          boxShadow: `0 4px 20px rgba(0,0,0,${intensity * 0.3})`
        }}
      >
        {isLeft ? 'NOCHMAL!' : 'GUT!'}
      </div>
    </div>
  );
};

export default SwipeIndicators;